import type {
    ActivityFilterItem,
    ActivityFilterOption
} from './activityFilter.utils';
import { activityKey, categoryLabels } from './activityFilter.utils';
import { ActivityType } from '../../../../core/api/FitnessActivityClient';

type FilterSummaryArgs = {
    isLoading: boolean;
    hasError: boolean;
    availableOptions: ActivityFilterOption[];
    selectedActivities: ActivityFilterItem[];
};

export const buildAvailableMap = (availableOptions: ActivityFilterOption[]) =>
    new Map<string, ActivityFilterOption>(
        availableOptions.map((option) => [option.key, option])
    );

export const buildSelectedKeys = (selectedActivities: ActivityFilterItem[]) =>
    selectedActivities.map((activity) =>
        activityKey(activity.category, activity.subType)
    );

export const buildSelectionsFromKeys = (
    nextKeys: string[],
    availableMap: Map<string, ActivityFilterOption>
) =>
    nextKeys
        .map((key) => availableMap.get(key))
        .filter((option): option is ActivityFilterOption => Boolean(option))
        .map((option) => ({
            category: option.category,
            subType: option.subType,
            label: option.label
        }));

export const getActivityFilterSummary = ({
    isLoading,
    hasError,
    availableOptions,
    selectedActivities
}: FilterSummaryArgs) => {
    if (isLoading) return 'Loading activities';
    if (hasError) return 'Activities unavailable';
    if (availableOptions.length === 0) return 'No activities available';
    if (selectedActivities.length === 0) return 'All activities';
    if (selectedActivities.length === availableOptions.length)
        return 'All activities selected';
    return `${selectedActivities.length}/${availableOptions.length} activities selected`;
};

export const groupOptionsByCategory = (
    availableOptions: ActivityFilterOption[]
) => {
    const groups: Record<string, ActivityFilterOption[]> = {
        [categoryLabels[ActivityType.DAILY_STEPS]]: [],
        [categoryLabels[ActivityType.FACILITY_VISIT]]: [],
        [categoryLabels[ActivityType.WORKOUT]]: []
    };

    availableOptions.forEach((activity) => {
        if (activity.category === ActivityType.DAILY_STEPS) {
            groups[categoryLabels[ActivityType.DAILY_STEPS]].push(activity);
        } else if (activity.category === ActivityType.FACILITY_VISIT) {
            groups[categoryLabels[ActivityType.FACILITY_VISIT]].push(activity);
        } else if (activity.category === ActivityType.WORKOUT) {
            groups[categoryLabels[ActivityType.WORKOUT]].push(activity);
        }
    });

    Object.values(groups).forEach((activities) => {
        activities.sort((a, b) => a.label.localeCompare(b.label));
    });

    return groups;
};
